import React from "react";
import { Rating, RatingProps } from "@mui/material";
import { Tooltip } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import styled from "styled-components";
import { DataFormControl } from "../DataFormControl";
import { PropertyEditorInteger } from "../../../CommonUI/PropertyEditing/PropertyEditorInteger";
import { PropertyEditorText } from "../../../CommonUI/PropertyEditing/PropertyEditorText";
import { PropertyEditorBoolean } from "../../../CommonUI/PropertyEditing/PropertyEditorBoolean";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { NumberDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/NumberDataDefinition";
import { StyledFormHelperText } from "./CommonStyled";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { PropertyEditorCollapse } from "../../../CommonUI/PropertyEditing/PropertyEditorCollapse";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";

const StyledRatingWrapper = styled.div<{ showLabels: boolean }>`
    padding-bottom: ${(props) => (props.showLabels ? "20px" : "0px")};
`;

const StyledRating = (rightContent: string, leftContent: string, size: RatingProps["size"], isLabels: boolean) =>
    withStyles({
        root: {
            "&::before": isLabels
                ? {
                      content: `"${leftContent}"`,
                      fontFamily: UIStyleConstants.FONT_FAMILY,
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: "14px",
                      color: UIStyleConstants.GRAY_DARK_COLOR,
                      position: "absolute",
                      top: size === "small" ? "15px" : "28px",
                  }
                : {
                      display: "none",
                  },
            "&::after": isLabels
                ? {
                      content: `"${rightContent}"`,
                      fontFamily: UIStyleConstants.FONT_FAMILY,
                      fontStyle: "normal",
                      fontWeight: 400,
                      fontSize: "14px",
                      color: UIStyleConstants.GRAY_DARK_COLOR,
                      position: "absolute",
                      top: size === "small" ? "15px" : "28px",
                      right: "0px",
                  }
                : {
                      display: "none",
                  },
        },
    })(Rating);

class XRatingEditUI extends React.Component<{ editMe: RatingFormControl }> {
    render() {
        return (
            <UISpacedBoxContent>
                {this.props.editMe.renderBaseDataControlEditor()}
                <PropertyEditorInteger
                    label={"Star Count"}
                    propertyName={"count"}
                    editObject={this.props.editMe}
                />
                <PropertyEditorBoolean
                    label={"Show Labels"}
                    propertyName={"showLabels"}
                    editObject={this.props.editMe}
                />
                <PropertyEditorCollapse
                    editObject={this.props.editMe}
                    collapseLogicOrField={"showLabels"}
                >
                    <PropertyEditorText
                        label="Left Text"
                        propertyName={"left"}
                        editObject={this.props.editMe}
                    />
                    <PropertyEditorText
                        label="Right Text"
                        propertyName={"right"}
                        editObject={this.props.editMe}
                    />
                </PropertyEditorCollapse>
            </UISpacedBoxContent>
        );
    }
}

export class RatingFormControl extends DataFormControl {
    readonly __type: string = "Rating";

    count: number = 10;
    left: string = "Worst";
    right: string = "Best";
    showLabels: boolean = true;
    size?: RatingProps["size"] = undefined;

    constructor() {
        super();
        this.canFocus = false;
    }

    render() {
        const RatingComponent = StyledRating(this.right, this.left, this.size, this.showLabels);
        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        return (
            <>
                {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                <StyledRatingWrapper showLabels={this.showLabels}>
                    <RatingComponent
                        name="rating-controller"
                        value={this.getValue() || 0}
                        max={this.count}
                        onChange={(event, newValue) => this.handleChange(event, newValue)}
                        size={this.size}
                        precision={0.5}
                        data-testid={this.valueName}
                        ref={this.ref}
                    />
                </StyledRatingWrapper>

                {issueText && issueText.length > 30 && (
                    <Tooltip
                        title={issueText}
                        arrow
                        placement="bottom"
                    >
                        <StyledFormHelperText
                            error={Boolean(issueText)}
                            data-testid={`${this.valueName || "rating"}-hinttext`}
                        >
                            {issueText.slice(0, 30) + "..."}
                        </StyledFormHelperText>
                    </Tooltip>
                )}
                {issueText && issueText.length < 31 && (
                    <StyledFormHelperText
                        error={Boolean(issueText)}
                        data-testid={`${this.valueName || "rating"}-hinttext`}
                    >
                        {issueText}
                    </StyledFormHelperText>
                )}
            </>
        );
    }

    renderEditUI(): JSX.Element | null {
        return <XRatingEditUI editMe={this} />;
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new NumberDataDefinition();
        def.name = this.valueName;
        return def;
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "count", "left", "right", "showLabels", "size");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "count", "left", "right", "showLabels", "size");
    }

    private handleChange = (event: React.ChangeEvent<{}>, newValue: number | null) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, newValue);
        }
    };

    private getValue = () => {
        if (this.valueName) {
            return this.getFormDataValue(this.valueName);
        }
    };
}
